import * as React from 'react';
import * as _ from 'lodash';
import FormDialog, {FormDialogProps} from 'cl-dialogs/FormDialog';
import FormSelect from 'cl-form/FormSelect/FormSelect';
import FormInput from 'cl-form/FormInput/FormInput';
import {ColumnsType} from 'cl-table/redux/actions';
import {getColumnDisplayName} from 'cl-table/VirtualizedHelpers';
import {ExtendedWrappedFieldExternalProps} from 'cl-form/FormFields';
import {createValidateRequired} from 'cl-form/validation/validators';
import {getFormValues} from 'redux-form';
import {aspectValuesType} from '../EditVariationOptionsDialog/EditVariationOptionsDialog';
import {useSelector} from 'react-redux';

export type variationType = {VariationSpecifics: aspectValuesType};

export interface AddVariationFormData {
  Quantity: number;
  SKU: string;
  Variations: object[];
}

export interface AddVariationDialogProps extends FormDialogProps {
  columns: ColumnsType;
  variationsData: variationType[];
  variationAspectNames: string[];
  notMandatoryFields?: string[];
}

const AddVariationDialog: React.FunctionComponent<AddVariationDialogProps> = ({
  columns,
  variationAspectNames,
  variationsData,
  notMandatoryFields = [],
  formName,
  ...props
}) => {
  const formValues = useSelector(getFormValues(formName));

  const isVariationMatchesFormValues = (specifics, formValues) => {
    if (_.isEmpty(formValues)) {
      return true;
    }
    return !_.some(
      specifics,
      (specificValues, specificName) => formValues[specificName] && formValues[specificName] !== specificValues
    );
  };

  const getRecommendations = specific => {
    const recommendations = [];
    _.forEach(variationsData, variation => {
      const specifics = variation.VariationSpecifics;
      if (isVariationMatchesFormValues(specifics, _.omit(formValues, specific))) {
        recommendations.push(specifics[specific]);
      }
    });
    return _.uniq(recommendations);
  };

  const getMappedOptions = specific => {
    const recommendations = getRecommendations(specific);
    return _.isEmpty(recommendations)
      ? undefined
      : _.map(recommendations, recommendation => ({
          value: recommendation,
        }));
  };

  const buildFieldsFromColumns = (): ExtendedWrappedFieldExternalProps[] => {
    const formInputColumns = _.omit(columns, variationAspectNames);
    return _.map(
      formInputColumns,
      (columnInfo, columnName): ExtendedWrappedFieldExternalProps => ({
        component: FormInput,
        name: columnName,
        label: getColumnDisplayName(columnName, columnInfo),
      })
    );
  };

  const buildFieldsFromVariationsData = (): ExtendedWrappedFieldExternalProps[] => {
    return _.map(
      variationAspectNames,
      (columnName): ExtendedWrappedFieldExternalProps => ({
        component: FormSelect,
        name: columnName,
        label: getColumnDisplayName(columnName, columns[columnName]),
        props: {
          clearable: true,
          options: getMappedOptions(columnName),
        },
      })
    );
  };

  const fields = [...buildFieldsFromVariationsData(), ...buildFieldsFromColumns()];
  return (
    <FormDialog
      validate={createValidateRequired(_.filter(fields, field => !_.includes(notMandatoryFields, field.name)))}
      title="Add Variation"
      fields={fields}
      formName={formName}
      {...props}
    />
  );
};

export default AddVariationDialog;
